import { useEffect, useRef, useState } from "react";
import { ColumnDTO } from "@retrospecter/shared/types";
import { useTranslation } from "../../i18n";
import { accentFor } from "./columnAccents";

interface NotePileProps {
  columns: ColumnDTO[];
  /** Scopes column translations to this board's format. */
  templateName: string;
  onDragStart: () => void;
  onDragEnd: () => void;
  /** Keyboard path: pick a column outright instead of dragging to it. */
  onChooseColumn: (columnId: string) => void;
}

/**
 * A pad of blank notes to drag onto a column.
 *
 * Dragging is the point, but it is mouse-only, so the pile is also a real
 * button: activating it opens a list of columns. Without that, adding a note
 * would be impossible without a pointer.
 */
export function NotePile({
  columns,
  templateName,
  onDragStart,
  onDragEnd,
  onChooseColumn,
}: NotePileProps) {
  const { t, columnTitle } = useTranslation();
  const [choosing, setChoosing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!choosing) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setChoosing(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setChoosing(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [choosing]);

  return (
    <div className="flex items-center gap-3" ref={containerRef}>
      <div className="relative h-14 w-14 flex-shrink-0">
        {/* Sheets underneath, outlined in the page colour so the stack reads as
            separate pieces of paper rather than one block. */}
        <span
          aria-hidden="true"
          className="absolute left-2 top-1.5 h-12 w-12 rotate-[10deg] rounded-lg bg-nm-accent/35 ring-2 ring-canvas"
        />
        <span
          aria-hidden="true"
          className="absolute -left-1.5 top-0.5 h-12 w-12 -rotate-[7deg] rounded-lg bg-nm-accent/60 ring-2 ring-canvas"
        />

        <button
          draggable
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = "copy";
            // Firefox will not start a drag without data on the transfer.
            event.dataTransfer.setData("text/plain", "new-note");
            onDragStart();
          }}
          onDragEnd={onDragEnd}
          onClick={() => setChoosing((open) => !open)}
          aria-label={t("column.pileLabel")}
          aria-expanded={choosing}
          data-testid="note-pile"
          className="relative flex h-12 w-12 cursor-grab items-center justify-center rounded-lg bg-nm-accent text-xl font-bold text-white shadow-raised ring-2 ring-canvas transition-transform duration-150 hover:-translate-y-1 hover:rotate-3 active:cursor-grabbing active:scale-95"
        >
          <span aria-hidden="true">+</span>
        </button>

        {choosing && (
          <div
            role="menu"
            className="animate-popIn absolute left-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-float"
          >
            {columns.map((column, index) => {
              const title = columnTitle(column.title, templateName);
              return (
                <button
                  key={column.id}
                  role="menuitem"
                  onClick={() => {
                    setChoosing(false);
                    onChooseColumn(column.id);
                  }}
                  aria-label={t("column.pileChoose", { column: title })}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink-soft transition-colors hover:bg-surface-sunken hover:text-ink"
                >
                  <span
                    aria-hidden="true"
                    className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${accentFor(index).bar}`}
                  />
                  <span className="truncate">{title}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs leading-snug text-ink-faint">{t("column.pileHint")}</p>
    </div>
  );
}
